import * as React from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Link } from "~/components/ui/link";
import { NumberPicker } from "~/components/ui/number-picker";
import { PriceDisplay } from "~/components/ui/price-display";
import { Slider } from "~/components/ui/slider";
import { useLocalizedPath } from "~/lib/use-locale-preference";
import type { FleetQuoteBuilderContent } from "~/data/products/types";
import totalSlash from "~/assets/products/drive-fleet/total-slash.svg";

export const FLEET_QUOTE_MIN = 0;
export const FLEET_QUOTE_MAX = 100;
// Designer-confirmed starting fleet size.
export const FLEET_QUOTE_DEFAULT = 25;
const TICKS = [0, 25, 50, 75, 100];

// Where the slider thumb's centre sits for a value: Radix keeps the 24px thumb inside the
// track, so its centre runs from 12px in at min to 12px in at max, not edge to edge. The
// number picker and the tick labels both follow it, so they line up with the thumb.
function thumbCenter(value: number) {
  const fraction = (value - FLEET_QUOTE_MIN) / (FLEET_QUOTE_MAX - FLEET_QUOTE_MIN);
  return `calc(12px + (100% - 24px) * ${fraction})`;
}

export function clampFleetSize(value: number) {
  return Math.min(FLEET_QUOTE_MAX, Math.max(FLEET_QUOTE_MIN, Math.round(value)));
}

// /fleet/quote's QuoteFormEmbed already reads `qty`. The email isn't passed on yet:
// whether the hosted form can prefill it is still an open question (BNC-5702 doc).
export function buildFleetQuoteHref(quotePath: string, qty: number) {
  return `${quotePath}?${new URLSearchParams({ qty: String(qty) })}`;
}

interface FleetQuoteBuilderProps {
  content: FleetQuoteBuilderContent;
  /** Per-device price, or null until the catalog loads (the total is hidden then). */
  unitPrice: number | null;
  currencySymbol: string;
  currencyCode: string;
}

export function FleetQuoteBuilder({ content, unitPrice, currencySymbol, currencyCode }: FleetQuoteBuilderProps) {
  const { t } = useTranslation("products");
  const navigate = useNavigate();
  const quotePath = useLocalizedPath("/fleet/quote");
  const [qty, setQty] = React.useState(FLEET_QUOTE_DEFAULT);
  const set = (value: number) => setQty(clampFleetSize(value));

  return (
    <div className="flex w-full flex-col gap-2">
      <form
        data-sid="fleet-quote-builder"
        className="bg-bouncie-100 text-bouncie-900 flex w-full flex-col gap-4 rounded-md p-4 lg:px-6"
        onSubmit={(event) => {
          event.preventDefault();
          navigate(buildFleetQuoteHref(quotePath, qty));
        }}
      >
        <div className="flex flex-col gap-4">
          <p id="fleet-quote-size-label" className="text-base leading-5 font-bold tracking-[0.16px]">
            {t(content.sizeLabel)}
          </p>
          <div className="flex flex-col gap-3">
            {/* The picker rides above the thumb; clamp() keeps the 90px picker inside the card. */}
            <div className="relative pt-10">
              <div
                className="absolute top-0 -translate-x-1/2"
                style={{ left: `clamp(45px, ${thumbCenter(qty)}, calc(100% - 45px))` }}
              >
                <NumberPicker
                  value={qty}
                  min={FLEET_QUOTE_MIN}
                  max={FLEET_QUOTE_MAX}
                  onIncrement={() => set(qty + 1)}
                  onDecrement={() => set(qty - 1)}
                  className="bg-bouncie-900 border-bouncie-900 h-8 w-[90px] text-white [&_button]:w-7 [&_button]:px-1 [&_button:hover]:bg-white/10"
                />
              </div>
              <Slider
                value={[qty]}
                min={FLEET_QUOTE_MIN}
                max={FLEET_QUOTE_MAX}
                step={1}
                onValueChange={([value]) => set(value)}
                aria-labelledby="fleet-quote-size-label"
                thumbLabel={t(content.sizeLabel)}
                className="h-6"
              />
            </div>
            {/* Each label is centred under the thumb's position for its value, not spread by
                justify-between, which would drift ~10px off the thumb at 25 and 75. */}
            <div aria-hidden className="relative h-5 text-sm leading-5">
              {TICKS.map((tick) => (
                <span key={tick} className="absolute top-0 -translate-x-1/2" style={{ left: thumbCenter(tick) }}>
                  {tick}
                </span>
              ))}
            </div>
          </div>
        </div>

        <hr className="border-t-[0.5px] border-[#273142]/50" />

        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-end justify-between whitespace-nowrap">
              <p className="font-display text-[18px] leading-[1.15] font-extrabold lg:font-sans lg:text-base lg:leading-5 lg:font-bold lg:tracking-[0.16px]">
                {t(content.totalLabel)}
              </p>
              <p className="text-xs leading-[18px]">{t(content.devicesLabel, { count: qty })}</p>
            </div>
            {unitPrice != null && (
              <div className="relative">
                <PriceDisplay
                  value={unitPrice * qty}
                  currencySymbol={currencySymbol}
                  currencyCode={currencyCode}
                  fontSize={18}
                  weightClassName="font-bold"
                />
                {/* eslint-disable-next-line local/no-raw-img -- decorative SVG strike-through */}
                <img src={totalSlash} alt="" className="pointer-events-none absolute inset-x-0 top-0 h-[17px] w-full" />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <Input
              data-sid="fleet-quote-builder-email"
              type="email"
              autoComplete="email"
              aria-label={t(content.emailLabel)}
              placeholder={t(content.emailLabel)}
              className="text-bouncie-900 h-9 border-[#d6d8db] bg-white px-3 py-2 text-sm leading-5 shadow-none placeholder:text-[#6c717a]"
            />
            <Button
              data-sid="fleet-quote-builder-submit"
              type="submit"
              variant="primary"
              className="h-11 w-full text-base leading-5 tracking-[0.16px] lg:h-[52px]"
            >
              {t(content.submitLabel)}
            </Button>
          </div>
        </div>
      </form>

      <div className="flex items-center justify-center gap-3 py-2 lg:justify-end lg:px-6">
        <p className="text-text-body-secondary text-xs leading-[18px]">{t(content.moreLabel)}</p>
        <Link
          data-sid="fleet-quote-builder-contact"
          to="/contact"
          className="text-bouncie-200 flex items-center gap-1 text-sm leading-5 font-bold tracking-[0.14px] underline"
        >
          {t(content.moreCtaLabel)}
          <RiArrowRightSLine aria-hidden className="size-5" />
        </Link>
      </div>
    </div>
  );
}
